import { useMemo, useState, useEffect, useRef } from 'react';

/**
 * Computes a normalized JSON fingerprint of the draft form.
 * Excludes transient UI state so preview navigation/zoom does not reset approval,
 * while any content/persisted mutation immediately resets approval.
 */
export function computeDraftFingerprint(form, section) {
  if (!form) return '';
  const clean = { ...form };
  // Remove transient UI properties
  delete clean._uiTab;
  delete clean._previewViewport;
  delete clean._previewTab;
  delete clean._scale;

  // Normalize gallery for products
  if (section === 'products' && Array.isArray(clean.gallery)) {
    clean.gallery = clean.gallery.filter(Boolean);
  }
  // Normalize page_items for pages/navigation
  if (Array.isArray(clean.page_items)) {
    clean.page_items = clean.page_items.map((item, idx) => ({
      block_type: item.block_type || 'section',
      eyebrow: item.eyebrow || '',
      title: item.title || '',
      description: item.description || '',
      image: item.image || '',
      alt: item.alt || '',
      href: item.href || '',
      cta_label: item.cta_label || '',
      display_order: idx,
    }));
  }
  // Sort keys deterministically
  return JSON.stringify(clean, Object.keys(clean).sort());
}

/**
 * Validates the draft form against resource configuration and custom constraints.
 */
export function validateDraft(form, section, config) {
  const errors = [];
  if (!form) return errors;

  if (section === 'products') {
    if (!form.name || !String(form.name).trim()) {
      errors.push({ field: 'name', message: 'Product title is required' });
    }
    if (form.specifications && typeof form.specifications === 'string') {
      try {
        const parsed = JSON.parse(form.specifications);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          errors.push({ field: 'specifications', message: 'Specifications must be a valid JSON object' });
        }
      } catch {
        errors.push({ field: 'specifications', message: 'Specifications must be valid JSON (e.g. {"Display": "6.1 inch"})' });
      }
    }
    return errors;
  }

  // Standard resources
  if (config?.fields) {
    for (const [field, label, type, required] of config.fields) {
      if (required) {
        const val = form[field];
        if (val === undefined || val === null || String(val).trim() === '') {
          errors.push({ field, message: `${label} is required` });
        }
      }
    }
  }

  // Navigation custom constraints
  if (section === 'navigation') {
    const menuType = form.menu_type || (form.parent_id ? 'submenu' : 'main');
    const destType = form.destination_type || (form.linked_page_id || String(form.url || '').startsWith('/content/') ? 'dynamic' : form.url ? 'custom' : 'dropdown');
    if (menuType === 'submenu' && !form.parent_id) {
      errors.push({ field: 'parent_id', message: 'Choose a main menu for this submenu' });
    }
    if (menuType === 'submenu' && destType === 'dropdown') {
      errors.push({ field: 'destination_type', message: 'Submenu must open a dynamic page or an existing URL' });
    }
    if (destType === 'custom' && !String(form.url || '').trim()) {
      errors.push({ field: 'url', message: 'Destination URL is required for custom links' });
    }
  }

  // Pages route validation
  if (section === 'pages' && form.route) {
    const r = String(form.route).trim();
    if (!r.startsWith('/')) {
      errors.push({ field: 'route', message: 'Route must start with a slash (e.g. /about)' });
    }
    if (r.includes('?') || r.includes('#')) {
      errors.push({ field: 'route', message: 'Route cannot contain query (?) or fragment (#)' });
    }
  }

  return errors;
}

export function usePreviewApproval({
  form,
  section,
  config,
  isUploading = false,
  isLoading = false,
}) {
  const currentFingerprint = useMemo(() => computeDraftFingerprint(form, section), [form, section]);
  const [approvedFingerprint, setApprovedFingerprint] = useState(null);
  const prevFingerprintRef = useRef(currentFingerprint);

  const validationErrors = useMemo(() => validateDraft(form, section, config), [form, section, config]);
  const isValid = validationErrors.length === 0;

  // Whenever the draft changes after an approval, reset the approval!
  useEffect(() => {
    if (prevFingerprintRef.current !== currentFingerprint) {
      prevFingerprintRef.current = currentFingerprint;
      if (approvedFingerprint !== null && approvedFingerprint !== currentFingerprint) {
        setApprovedFingerprint(null);
      }
    }
  }, [currentFingerprint, approvedFingerprint]);

  const isApproved = approvedFingerprint !== null && approvedFingerprint === currentFingerprint;
  const canApprove = isValid && !isUploading && !isLoading;

  const approvePreview = () => {
    if (!canApprove) return false;
    setApprovedFingerprint(currentFingerprint);
    return true;
  };

  const resetApproval = () => {
    setApprovedFingerprint(null);
  };

  return {
    isApproved,
    canApprove,
    validationErrors,
    isValid,
    approvedFingerprint,
    currentFingerprint,
    approvePreview,
    resetApproval,
  };
}
